/**
 * ReHub Studio pricing — single source of truth (per STUDIO_MASTER spec).
 *
 * Studio is priced in CASH (pence), not credits. Wallet credits may offset up
 * to 100% of any order at a fixed £0.75/credit; credits never set the price.
 *
 * IMPORTANT: the database mirror of these prices lives in the seeded
 * `studio_service_prices` table (see
 * `supabase/migrations/20260610120000_studio_v2.sql`). Edge functions cannot
 * import this package, so server-side totals are computed from that table —
 * keep both in sync when changing any price here.
 */

export type StudioTier = 'standard' | 'premium' | 'ultra';
export type StudioTurnaround = 'standard' | 'priority' | 'express';
export type StudioCurrency = 'GBP';

export const STUDIO_TIERS: StudioTier[] = ['standard', 'premium', 'ultra'];

export const STUDIO_TIER_LABELS: Record<StudioTier, string> = {
  standard: 'Standard',
  premium: 'Premium',
  ultra: 'Ultra',
};

// =============================================================================
// Tiered services (composite — depth + speed blended)
// =============================================================================

export interface StudioTierDef {
  tier: StudioTier;
  /** Price in pence (GBP). Per unit for per-unit services, per project otherwise. */
  pricePence: number;
  /** Marketing copy of what the tier includes. */
  includes: string[];
  /** Baseline SLA already included in the tier price. */
  sla: string;
}

export interface StudioTieredServiceDef {
  key: string;
  name: string;
  description: string;
  /** True when the price is per unit (e.g. virtual staging per image). */
  perUnit: boolean;
  /** Label for the quantity unit, e.g. "images". */
  unitLabel: string;
  defaultQuantity: number;
  tiers: StudioTierDef[];
}

export const STUDIO_TIERED_SERVICES: StudioTieredServiceDef[] = [
  {
    key: 'imagemotion',
    name: 'ImageMotion',
    description: 'Cinematic motion video created from your still property images.',
    perUnit: false,
    unitLabel: 'videos',
    defaultQuantity: 1,
    tiers: [
      {
        tier: 'standard',
        pricePence: 5900,
        includes: ['Up to 10 images', 'Basic motion', 'Dissolve transitions', 'Simple title'],
        sla: '48–72h',
      },
      {
        tier: 'premium',
        pricePence: 11000,
        includes: ['Up to 20 images', 'Cinematic motion', 'Multiple transitions', 'Text overlays', 'Intro/outro', 'Music', 'Branding'],
        sla: '~36h',
      },
      {
        tier: 'ultra',
        pricePence: 19500,
        includes: ['Up to 30 images', 'Full cinematic sequence', 'Advanced effects', 'Full branding control'],
        sla: '~24h',
      },
    ],
  },
  {
    key: 'tour_build',
    name: '360 Tour Build',
    description: 'An interactive virtual tour assembled from your 360 captures.',
    perUnit: false,
    unitLabel: 'tours',
    defaultQuantity: 1,
    tiers: [
      {
        tier: 'standard',
        pricePence: 9900,
        includes: ['Up to 30 panos', 'Clean build', 'Basic hotspots'],
        sla: '48–72h',
      },
      {
        tier: 'premium',
        pricePence: 16000,
        includes: ['Everything in Standard', 'Agent narration', 'Branded skin', 'Custom hotspots'],
        sla: '~36h',
      },
      {
        tier: 'ultra',
        pricePence: 28500,
        includes: ['Everything in Premium', 'Multi-language', 'Floor-plan integration', 'Showcase wrapper', 'Priority SLA'],
        sla: '~24h',
      },
    ],
  },
  {
    key: 'planos',
    name: 'Planos Interactive',
    description: 'A polished, interactive floor plan produced from your sketches or measurements.',
    perUnit: false,
    unitLabel: 'floor plans',
    defaultQuantity: 1,
    tiers: [
      {
        tier: 'standard',
        pricePence: 5900,
        includes: ['Single interactive floor plan'],
        sla: '48–72h',
      },
      {
        tier: 'premium',
        pricePence: 10000,
        includes: ['Everything in Standard', 'Room labels', 'Furniture layer', 'Branding'],
        sla: '~36h',
      },
      {
        tier: 'ultra',
        pricePence: 16500,
        includes: ['Everything in Premium', 'Multi-floor', 'Measurements', '3D view', 'Tour/Showcase integration'],
        sla: '~24h',
      },
    ],
  },
  {
    key: 'showcase',
    name: 'Showcase',
    description: 'A branded property microsite to present the listing online.',
    perUnit: false,
    unitLabel: 'microsites',
    defaultQuantity: 1,
    tiers: [
      {
        tier: 'standard',
        pricePence: 7900,
        includes: ['Single-listing microsite'],
        sla: '48–72h',
      },
      {
        tier: 'premium',
        pricePence: 16000,
        includes: ['Agency Showcase', 'Custom domain', 'Branding', 'Lead capture'],
        sla: '~36h',
      },
      {
        tier: 'ultra',
        pricePence: 31500,
        includes: ['Everything in Premium', 'CRM/portal integrations', 'Multi-listing', 'Analytics', 'Priority support'],
        sla: '~24h',
      },
    ],
  },
  {
    key: 'videomotion',
    name: 'VideoMotion',
    description: 'Professionally edited property video from your raw footage.',
    perUnit: false,
    unitLabel: 'videos',
    defaultQuantity: 1,
    tiers: [
      {
        tier: 'standard',
        pricePence: 7900,
        includes: ['Standard edited video'],
        sla: '48–72h',
      },
      {
        tier: 'premium',
        pricePence: 13500,
        includes: ['Everything in Standard', 'Branding', 'Music', 'Longer cut'],
        sla: '~36h',
      },
      {
        tier: 'ultra',
        pricePence: 24000,
        includes: ['Everything in Premium', 'Advanced edit', 'Multiple versions', 'Priority SLA'],
        sla: '~24h',
      },
    ],
  },
  {
    key: 'virtual_staging',
    name: 'Virtual Staging',
    description: 'Furnish empty rooms with realistic virtual furniture and styling.',
    perUnit: true,
    unitLabel: 'images',
    defaultQuantity: 3,
    tiers: [
      {
        tier: 'standard',
        pricePence: 750,
        includes: ['AI-managed', '1 style'],
        sla: '48–72h',
      },
      {
        tier: 'premium',
        pricePence: 1200,
        includes: ['Style choice', 'Declutter', 'QA pass'],
        sla: '~36h',
      },
      {
        tier: 'ultra',
        pricePence: 1900,
        includes: ['Signature — designer-grade', 'Multiple revisions'],
        sla: '~24h',
      },
    ],
  },
];

// =============================================================================
// Flat services (simple — no tiers, priced per unit)
// =============================================================================

export interface StudioFlatServiceDef {
  key: string;
  name: string;
  description: string;
  /** Price per unit in pence (GBP). */
  pricePence: number;
  unitLabel: string;
  defaultQuantity: number;
  /** Marked as an add-on (narration, language) rather than a standalone line. */
  addOn?: boolean;
}

export const STUDIO_FLAT_SERVICES: StudioFlatServiceDef[] = [
  {
    key: 'photo_enhancement',
    name: 'Photo Enhancement',
    description: 'Professional colour, exposure and clarity correction.',
    pricePence: 150,
    unitLabel: 'images',
    defaultQuantity: 10,
  },
  {
    key: 'photoclear',
    name: 'Photoclear',
    description: 'Remove clutter, personal items and distractions from photos.',
    pricePence: 250,
    unitLabel: 'images',
    defaultQuantity: 10,
  },
  {
    key: 'hdr_enhancement',
    name: 'HDR Enhancement',
    description: 'Professional HDR bracket merging and enhancement.',
    pricePence: 250,
    unitLabel: 'images',
    defaultQuantity: 10,
  },
  {
    key: 'hyperdusk_image',
    name: 'HyperDusk Image',
    description: 'Day-to-dusk twilight conversion of a hero image.',
    pricePence: 1900,
    unitLabel: 'images',
    defaultQuantity: 1,
  },
  {
    key: 'hyperdusk_video',
    name: 'HyperDusk Video',
    description: 'Day-to-dusk twilight video conversion.',
    pricePence: 3900,
    unitLabel: 'videos',
    defaultQuantity: 1,
  },
  {
    key: 'sketchplan_residential',
    name: 'Sketchplan Residential',
    description: 'Residential floor plan drawn from your sketch or scan.',
    pricePence: 2900,
    unitLabel: 'plans',
    defaultQuantity: 1,
  },
  {
    key: 'sketchplan_commercial',
    name: 'Sketchplan Commercial',
    description: 'Commercial floor plan drawn from your sketch or scan (from £49).',
    pricePence: 4900,
    unitLabel: 'plans',
    defaultQuantity: 1,
  },
  {
    key: 'agent_narration',
    name: 'Agent Narration',
    description: 'Professional voiceover narration added to a video or tour.',
    pricePence: 2900,
    unitLabel: 'recordings',
    defaultQuantity: 1,
    addOn: true,
  },
  {
    key: 'additional_language',
    name: 'Additional Language',
    description: 'An extra language version of a narrated deliverable.',
    pricePence: 1900,
    unitLabel: 'languages',
    defaultQuantity: 1,
    addOn: true,
  },
  {
    key: 'social_pack',
    name: 'Social Pack',
    description: 'A set of branded, ready-to-post social media assets for the listing.',
    pricePence: 3500,
    unitLabel: 'packs',
    defaultQuantity: 1,
  },
];

// =============================================================================
// Turnaround surcharges (rush beyond the tier's baseline SLA)
// =============================================================================

export interface StudioTurnaroundDef {
  key: StudioTurnaround;
  label: string;
  sla: string;
  /** Surcharge as a fraction of the order subtotal (0 = included). */
  surcharge: number;
}

export const STUDIO_TURNAROUNDS: StudioTurnaroundDef[] = [
  { key: 'standard', label: 'Standard', sla: '48–72h', surcharge: 0 },
  { key: 'priority', label: 'Priority', sla: '24–48h', surcharge: 0.5 },
  { key: 'express', label: 'Express', sla: '<24h', surcharge: 1 },
];

export const STUDIO_TURNAROUNDS_BY_KEY: Record<StudioTurnaround, StudioTurnaroundDef> =
  Object.fromEntries(STUDIO_TURNAROUNDS.map((t) => [t.key, t])) as Record<
    StudioTurnaround,
    StudioTurnaroundDef
  >;

// =============================================================================
// Credits-as-discount + order policies
// =============================================================================

/** Fixed cash value of one wallet credit when offsetting a Studio order. */
export const STUDIO_CREDIT_VALUE_PENCE = 75;

/**
 * Credits may offset at most this fraction of any Studio order. Set to 1 so a
 * customer with enough wallet credits can cover the whole order (the £25
 * minimum order value still applies to the cash total before any offset).
 */
export const STUDIO_MAX_CREDIT_OFFSET = 1;

/** Minimum order value. Sub-£25 needs go DIY or into a bundle. */
export const STUDIO_MIN_ORDER_PENCE = 2500;

/** Revision policy per the master spec. */
export const STUDIO_REVISION_POLICY = {
  /** Rounds included per item on Standard/Premium tiers. */
  includedRounds: 1,
  /** Rounds included per item on Ultra tiers. */
  includedRoundsUltra: 2,
  /** Extra rounds cost this fraction of the line price… */
  extraRoundFraction: 0.25,
  /** …with this minimum charge in pence. */
  extraRoundMinPence: 1500,
  /** Days after delivery to request a revision, else auto-approved. */
  requestWindowDays: 7,
} as const;

// =============================================================================
// Bundles ("Listing kits") + subscriptions
//
// Bundles are purchasable as a single fixed-price line item. The fixed price is
// the source of truth (mirrored into studio_service_prices for server-side
// charging); `contents` is the human-readable scope the studio team fulfils.
// Subscriptions remain data-only (no checkout yet).
// =============================================================================

export interface StudioBundleDef {
  key: string;
  name: string;
  pricePence: number;
  /** Approximate à la carte value of the contents, in pence. */
  contentsValuePence: number;
  contents: string[];
}

export const STUDIO_BUNDLES: StudioBundleDef[] = [
  {
    key: 'marketing_pack',
    name: 'Marketing Pack',
    pricePence: 19900,
    contentsValuePence: 26000,
    contents: [
      '25× Photo Enhancement',
      '10× HDR',
      '4× AI Staging (Standard)',
      '1× HyperDusk Video',
      '1× ImageMotion Standard',
      '2× Social Pack',
    ],
  },
  {
    key: 'sales_pack',
    name: 'Sales Pack',
    pricePence: 39900,
    contentsValuePence: 51900,
    contents: [
      '1× Tour Build Premium',
      '1× Planos Standard',
      '1× ImageMotion Premium',
      '30× Photo Enhancement',
      '15× HDR',
      '6× AI Staging (Premium)',
      '1× Social Pack',
    ],
  },
  {
    key: 'developer_pack',
    name: 'Developer Pack',
    pricePence: 99500,
    contentsValuePence: 137600,
    contents: [
      '1× Tour Build Ultra',
      '2× Planos Premium',
      '1× Showcase Premium',
      '1× ImageMotion Ultra',
      '1× VideoMotion Standard',
      '2× HyperDusk Video',
      '60× Photo Enhancement',
      '30× HDR',
      '12× AI Staging (Premium)',
      '2× Social Pack',
    ],
  },
  {
    key: 'luxury_pack',
    name: 'Luxury Pack',
    pricePence: 64900,
    contentsValuePence: 84500,
    contents: [
      '1× Tour Build Ultra',
      '1× Showcase Premium',
      '1× ImageMotion Ultra',
      '1× HyperDusk Video',
      '4× AI Staging (Ultra)',
      '20× Photo Enhancement',
      '10× HDR',
      '1× Social Pack',
    ],
  },
];

export interface StudioSubscriptionDef {
  key: 'lite' | 'growth' | 'enterprise';
  name: string;
  monthlyPricePence: number;
  /** Monthly production allowance measured in pence of à la carte value. */
  allowancePence: number;
  sla: string;
  blurb: string;
}

export const STUDIO_SUBSCRIPTIONS: StudioSubscriptionDef[] = [
  {
    key: 'lite',
    name: 'Studio Lite',
    monthlyPricePence: 29900,
    allowancePence: 45000,
    sla: 'Standard',
    blurb: '≈ 4–5 listings/month',
  },
  {
    key: 'growth',
    name: 'Studio Growth',
    monthlyPricePence: 59900,
    allowancePence: 100000,
    sla: 'Priority',
    blurb: '≈ 10–12 listings/month',
  },
  {
    key: 'enterprise',
    name: 'Studio Enterprise',
    monthlyPricePence: 149900,
    allowancePence: 300000,
    sla: 'Priority + dedicated manager',
    blurb: '≈ 30+ listings/month',
  },
];

// =============================================================================
// Lookups + price helpers
// =============================================================================

export const STUDIO_TIERED_SERVICES_BY_KEY: Record<string, StudioTieredServiceDef> =
  Object.fromEntries(STUDIO_TIERED_SERVICES.map((s) => [s.key, s]));

export const STUDIO_FLAT_SERVICES_BY_KEY: Record<string, StudioFlatServiceDef> =
  Object.fromEntries(STUDIO_FLAT_SERVICES.map((s) => [s.key, s]));

export const STUDIO_BUNDLES_BY_KEY: Record<string, StudioBundleDef> =
  Object.fromEntries(STUDIO_BUNDLES.map((b) => [b.key, b]));

export function isStudioTieredService(serviceKey: string): boolean {
  return serviceKey in STUDIO_TIERED_SERVICES_BY_KEY;
}

export function isStudioBundle(serviceKey: string): boolean {
  return serviceKey in STUDIO_BUNDLES_BY_KEY;
}

/**
 * Unit price in pence for a service line. Tiered services require a tier;
 * flat services and bundles ignore it (bundles are a single fixed price).
 * Returns null for unknown service/tier combos.
 */
export function studioUnitPricePence(serviceKey: string, tier?: StudioTier | null): number | null {
  const tiered = STUDIO_TIERED_SERVICES_BY_KEY[serviceKey];
  if (tiered) {
    const def = tiered.tiers.find((t) => t.tier === tier);
    return def ? def.pricePence : null;
  }
  const flat = STUDIO_FLAT_SERVICES_BY_KEY[serviceKey];
  if (flat) return flat.pricePence;
  const bundle = STUDIO_BUNDLES_BY_KEY[serviceKey];
  return bundle ? bundle.pricePence : null;
}

/** Total in pence for a service line (unit price × quantity). */
export function studioLinePricePence(
  serviceKey: string,
  tier: StudioTier | null | undefined,
  quantity: number
): number | null {
  const unit = studioUnitPricePence(serviceKey, tier);
  if (unit == null) return null;
  return unit * Math.max(1, Math.round(quantity));
}

export interface StudioOrderLineInput {
  serviceKey: string;
  tier?: StudioTier | null;
  quantity: number;
}

export interface StudioOrderTotals {
  subtotalPence: number;
  surchargePence: number;
  totalPence: number;
}

/** Order totals: subtotal of all lines + turnaround surcharge on the subtotal. */
export function studioOrderTotalPence(
  lines: StudioOrderLineInput[],
  turnaround: StudioTurnaround
): StudioOrderTotals {
  const subtotalPence = lines.reduce((sum, line) => {
    const price = studioLinePricePence(line.serviceKey, line.tier, line.quantity);
    return sum + (price ?? 0);
  }, 0);
  const surchargeRate = STUDIO_TURNAROUNDS_BY_KEY[turnaround]?.surcharge ?? 0;
  const surchargePence = Math.round(subtotalPence * surchargeRate);
  return { subtotalPence, surchargePence, totalPence: subtotalPence + surchargePence };
}

/**
 * Maximum credits that can be applied to an order: capped by STUDIO_MAX_CREDIT_OFFSET
 * of the cash total (at £0.75/credit) and by the user's wallet balance. With the
 * cap at 100%, a sufficiently funded wallet can cover the whole order.
 *
 * The credits needed are rounded UP so the final credit absorbs any sub-credit
 * remainder (order totals are rarely multiples of £0.75). The resulting cash
 * offset is then capped at the order total (see `studioCreditOffsetPence`), so
 * the amount due lands on exactly £0 instead of an unchargeable few-pence
 * remainder.
 */
export function maxStudioCreditOffset(totalPence: number, creditBalance: number): number {
  const capByOrder = Math.ceil((totalPence * STUDIO_MAX_CREDIT_OFFSET) / STUDIO_CREDIT_VALUE_PENCE);
  return Math.max(0, Math.min(capByOrder, Math.floor(creditBalance)));
}

/**
 * Cash discount in pence for a given number of applied credits, never more than
 * the order total (the last credit may be only partially used when the total is
 * not a clean multiple of £0.75). Pass `totalPence` to cap; omit for the raw value.
 */
export function studioCreditOffsetPence(creditsApplied: number, totalPence?: number): number {
  const raw = Math.max(0, Math.floor(creditsApplied)) * STUDIO_CREDIT_VALUE_PENCE;
  return totalPence == null ? raw : Math.min(raw, Math.max(0, totalPence));
}

/** Format pence as a GBP display string, e.g. 19900 → "£199", 750 → "£7.50". */
export function formatStudioPrice(pence: number): string {
  const pounds = pence / 100;
  return Number.isInteger(pounds) ? `£${pounds}` : `£${pounds.toFixed(2)}`;
}
